import { useCallback, useEffect, useState } from 'react';
import { Message } from '@oneflowab/pomes';
import { Form as ReactFinalForm } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import type { MessageTranslator } from '@oneflowab/pomes';
import type { match as Match } from 'react-router';

// eslint-disable-next-line import/named
import { hasAnyTag } from 'oneflow-client/tags';
import tagsReducer from 'reducers/entities/tags';

import adminPage from 'hocs/admin-page';
import Field from 'components/field';
import * as EditableForm from 'components/editable-form';
import { DetailsFooter } from 'components/details-footer';
import { LocalizedDateTime } from 'components/localized-date-time';
import { checkAcl } from 'components/acl';
import TextField from 'components/text-field';

import style from './tag-details.module.scss';

type UpdateTagData = {
  id: number,
  data: Oneflow.Tag
};

type LocationWithQuery = Location & {
  query?: {
    edit?: boolean;
  };
};

export type Props = {
  message: MessageTranslator,
  location: LocationWithQuery,
  match: Match
};

export function TagDetails({
  message,
  location,
  match,
}: Props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isEditMode, setIsEditMode] = useState(false);

  const tag = useSelector(
    (state) => tagsReducer.getTagSelector(state, { id: match.params.id }),
  );
  const updateState = useSelector(
    (state) => tagsReducer.getUpdateSelector(state, { id: tag.id }),
  );

  const resetUpdateState = useCallback(() => {
    dispatch(tagsReducer.updateTagReset({
      id: match.params.id,
    }));
  }, [dispatch, match.params.id]);

  const updateTag = (data: UpdateTagData) => {
    dispatch(tagsReducer.updateTag({
      id: match.params.id,
      data: {
        name: null,
        ...data,
      },
      pipe: {
        onSuccess: () => {
          setIsEditMode(false);
        },
      },
    }));
  };

  const checkEditPermission = useCallback(() => checkAcl(tag.acl, [
    'tag:update:name',
  ], { match: 'any' }), [tag.acl]);

  const handleEditMode = useCallback((newEditMode: boolean) => {
    resetUpdateState();
    setIsEditMode(newEditMode);
  }, [resetUpdateState]);

  const updatePath = useCallback((pathname: string) => {
    history.replace(pathname);
  }, [history]);

  const enableEditModeIfRedirected = useCallback(() => {
    if (!isEditMode && location?.query?.edit && checkEditPermission()) {
      updatePath(location.pathname);
      setIsEditMode(true);
    }
  }, [
    isEditMode,
    location?.query?.edit,
    checkEditPermission,
    updatePath,
    location?.pathname,
  ]);

  useEffect(() => {
    enableEditModeIfRedirected();
  }, [enableEditModeIfRedirected]);

  const uniqueTagName = (
    { params }: { params: any },
  ) => hasAnyTag({ params, entityId: tag.id });

  const getInitialValues = () => {
    const {
      name,
    } = tag;

    return {
      name,
    };
  };

  const infoPairs = [
    {
      label: <Message
        id="ID"
        comment="ID label for the details footer"
      />,
      value: tag.id,
    },
    {
      label: <Message
        id="Creation date"
        comment="Creation date label for the details footer"
      />,
      value: <LocalizedDateTime datetime={tag.createdTime} />,
    },
  ];

  return (
    <ReactFinalForm
      initialValues={getInitialValues()}
      onSubmit={updateTag}
      render={({ handleSubmit }) => (
        <form
          onSubmit={(values) => {
            handleSubmit(values);
          }}
        >
          <EditableForm.Header>
            <EditableForm.Actions
              resetFormState={resetUpdateState}
              formState={updateState}
              isEditMode={isEditMode}
              setIsEditMode={handleEditMode}
              disabled={!checkEditPermission()}
            />
          </EditableForm.Header>
          <EditableForm.Body>
            <div className={style.FormBody}>
              <h2 className={style.Title}>
                <Message
                  id="Tag details"
                  comment="Title for the user details form."
                />
              </h2>
              <Field
                id="name"
                name="name"
                component={TextField}
                value={tag.name}
                placeholder={message({
                  id: 'Enter tag name',
                  comment: 'Name placeholder for a tag.',
                })}
                autoFocus
                maxLength={30}
                required
                autoComplete="off"
                label={message({
                  id: 'Tag name',
                  comment: 'Label for the name field for a tag.',
                })}
                disabled={!isEditMode || !checkAcl(tag.acl, 'tag:update:name')}
                labelCustomClass={style.Label}
                unique={{
                  text: message({
                    id: 'A tag with this name already exists. Try another one.',
                    comment: 'Form validation error message.',
                  }),
                  param: 'name',
                  request: uniqueTagName,
                  initialValue: getInitialValues().name,
                }}
              />
            </div>
          </EditableForm.Body>
          <DetailsFooter infoPairs={infoPairs} />
        </form>
      )}
    />
  );
}

export default adminPage(({ props: { message } }) => ({
  title: message({ id: 'Details', comment: 'The page title' }),
  modules: [[]],
}))(TagDetails);
